import { all, call, put, takeLatest, takeEvery } from 'redux-saga/effects';
import Swal from 'sweetalert2';
import {
  GET_VENDOR_RETAIL_BOOKINGS,
  GET_VENDOR_UNPAID_RETAIL_BOOKINGS,
  GET_VENDOR_PAID_RETAIL_BOOKINGS,
  FIND_VENDOR_RETAIL_BOOKING,
  CREATE_VENDOR_RETAIL_BOOKING,
  CHECK_SLOT_AVAILABILITY,
  CREATE_RETAIL_CHECKOUT_SESSION,
  CANCEL_RETAIL_BOOKING,
  GET_VENUE_RETAIL_APPLICATIONS,
  UPDATE_RETAIL_APPLICATION_STATUS,
  VENUE_CANCEL_RETAIL_BOOKING,
} from './constants';
import {
  getVendorRetailBookingsSuccess,
  getVendorRetailBookingsFailed,
  getVendorUnpaidRetailBookingsSuccess,
  getVendorUnpaidRetailBookingsFailed,
  getVendorPaidRetailBookingsSuccess,
  getVendorPaidRetailBookingsFailed,
  createVendorRetailBookingSuccess,
  createVendorRetailBookingFailed,
  vendorRetailNotBooked,
  checkSlotAvailabilitySuccess,
  checkSlotAvailabilityFailed,
  createRetailCheckoutSessionSuccess,
  createRetailCheckoutSessionFailed,
  cancelRetailBookingSuccess,
  cancelRetailBookingFailed,
  getVenueRetailApplicationsSuccess,
  getVenueRetailApplicationsFailed,
  updateRetailApplicationStatusSuccess,
  updateRetailApplicationStatusFailed,
  venueCancelRetailBookingSuccess,
  venueCancelRetailBookingFailed,
} from './action';
import * as Service from './service';

function* getVendorRetailBookingsSaga({ payload }) {
  try {
    const bookings = yield call(Service.getVendorRetailBookingsByVendor, payload.vendorId);
    yield put(getVendorRetailBookingsSuccess(bookings));
  } catch (error) {
    yield put(getVendorRetailBookingsFailed(error.message));
  }
}

function* getVendorUnpaidRetailBookingsSaga({ payload }) {
  try {
    const bookings = yield call(Service.getVendorUnpaidRetailBookings, payload.vendorId);
    yield put(getVendorUnpaidRetailBookingsSuccess(bookings));
  } catch (error) {
    yield put(getVendorUnpaidRetailBookingsFailed(error.message));
  }
}

function* getVendorPaidRetailBookingsSaga({ payload }) {
  try {
    const bookings = yield call(Service.getVendorPaidRetailBookings, payload.vendorId);
    yield put(getVendorPaidRetailBookingsSuccess(bookings));
  } catch (error) {
    yield put(getVendorPaidRetailBookingsFailed(error.message));
  }
}

function* findVendorRetailBookingSaga({ payload }) {
  try {
    const bookings = yield call(Service.getVendorRetailBookingsByVendor, payload.vendorId);
    // Find booking matching the schedule and date
    const booking = bookings.find(
      (b) =>
        b.retail_schedule_id === payload.retailScheduleId &&
        b.booking_date === payload.bookingDate &&
        !b.has_been_cancelled,
    );
    if (booking) {
      yield put(createVendorRetailBookingSuccess(booking));
    } else {
      yield put(vendorRetailNotBooked());
    }
  } catch (error) {
    yield put(getVendorRetailBookingsFailed(error.message));
  }
}

function* createVendorRetailBookingSaga({ payload }) {
  try {
    const booking = yield call(Service.createVendorRetailBooking, payload);
    console.log('🟢 [Saga] Created booking:', booking);
    yield put(createVendorRetailBookingSuccess(booking));
  } catch (error) {
    console.error('🔴 [Saga] Error creating booking:', error);
    yield put(createVendorRetailBookingFailed(error.message));
  }
}

function* checkSlotAvailabilitySaga({ payload }) {
  try {
    const result = yield call(
      Service.checkSlotAvailability,
      payload.retailScheduleId,
      payload.bookingDate,
      payload.startTime,
      payload.endTime,
    );
    yield put(checkSlotAvailabilitySuccess(result.available));
  } catch (error) {
    yield put(checkSlotAvailabilityFailed(error.message));
  }
}

function* createRetailCheckoutSessionSaga({ payload }) {
  try {
    const session = yield call(Service.createRetailCheckoutSession, payload);
    yield put(createRetailCheckoutSessionSuccess(session));
    // Redirect to Stripe checkout
    if (session.url) {
      window.location.href = session.url;
    }
  } catch (error) {
    yield put(createRetailCheckoutSessionFailed(error.message));
  }
}

function* cancelRetailBookingSaga({ payload }) {
  try {
    const booking = yield call(
      Service.cancelRetailBooking,
      payload.bookingId,
      payload.vendorId,
      payload.reason,
    );
    yield put(cancelRetailBookingSuccess(booking));
  } catch (error) {
    yield put(cancelRetailBookingFailed(error.message));
  }
}

function* getVenueRetailApplicationsSaga({ payload }) {
  try {
    const applications = yield call(Service.getVenueRetailApplications, payload.venueId);
    yield put(getVenueRetailApplicationsSuccess(applications));
  } catch (error) {
    yield put(getVenueRetailApplicationsFailed(error.message));
    Swal.fire({
      title: 'Error',
      text: 'Failed to load retail applications',
      icon: 'error',
    });
  }
}

function* updateRetailApplicationStatusSaga({ payload }) {
  try {
    const { bookingId, newStatus, onStatusChanged } = payload;
    const booking = yield call(Service.updateRetailApplicationStatus, bookingId, newStatus);
    yield put(updateRetailApplicationStatusSuccess(booking));

    // Call callback if provided
    if (onStatusChanged) {
      onStatusChanged(booking);
    }

    Swal.fire({
      title: 'Success',
      text: `Application ${newStatus === 'approved' ? 'approved' : 'denied'} successfully`,
      icon: 'success',
      timer: 2000,
    });
  } catch (error) {
    yield put(updateRetailApplicationStatusFailed(error.message));
    Swal.fire({
      title: 'Error',
      text: 'Failed to update application status',
      icon: 'error',
    });
  }
}

function* venueCancelRetailBookingSaga({ payload }) {
  try {
    const { bookingId, venueId, reason, onCancelled } = payload;
    const booking = yield call(Service.venueCancelRetailBooking, bookingId, venueId, reason);
    yield put(venueCancelRetailBookingSuccess(booking));

    // Call callback if provided
    if (onCancelled) {
      onCancelled(booking);
    }

    Swal.fire({
      title: 'Success',
      text: 'Booking cancelled successfully',
      icon: 'success',
      timer: 2000,
    });
  } catch (error) {
    yield put(venueCancelRetailBookingFailed(error.message));
    Swal.fire({
      title: 'Error',
      text: 'Failed to cancel booking',
      icon: 'error',
    });
  }
}

export default function* vendorRetailSaga() {
  yield all([
    takeLatest(GET_VENDOR_RETAIL_BOOKINGS, getVendorRetailBookingsSaga),
    takeLatest(GET_VENDOR_UNPAID_RETAIL_BOOKINGS, getVendorUnpaidRetailBookingsSaga),
    takeLatest(GET_VENDOR_PAID_RETAIL_BOOKINGS, getVendorPaidRetailBookingsSaga),
    takeLatest(FIND_VENDOR_RETAIL_BOOKING, findVendorRetailBookingSaga),
    takeEvery(CREATE_VENDOR_RETAIL_BOOKING, createVendorRetailBookingSaga),
    takeLatest(CHECK_SLOT_AVAILABILITY, checkSlotAvailabilitySaga),
    takeLatest(CREATE_RETAIL_CHECKOUT_SESSION, createRetailCheckoutSessionSaga),
    takeLatest(CANCEL_RETAIL_BOOKING, cancelRetailBookingSaga),
    takeLatest(GET_VENUE_RETAIL_APPLICATIONS, getVenueRetailApplicationsSaga),
    takeLatest(UPDATE_RETAIL_APPLICATION_STATUS, updateRetailApplicationStatusSaga),
    takeLatest(VENUE_CANCEL_RETAIL_BOOKING, venueCancelRetailBookingSaga),
  ]);
}
